/**
 * Does this instrument's cap table still add up?
 *
 * Four independent counts of the same units - the instrument's own figure, the sum of holdings,
 * the units issued by cap table events, and the units allocated by subscriptions - are maintained
 * by different code paths. A disagreement is the earliest sign that an edit, a transfer or a
 * deletion left something behind, so it is surfaced here rather than found at audit.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle, Warning, ArrowsClockwise, CaretRight } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import { api } from '../../context/AuthContext';

const units = (value) => Number(value || 0).toLocaleString('en-GB');

const Figure = ({ label, value }) => (
  <div>
    <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-foreground-muted">{label}</p>
    <p className="text-sm font-semibold tabular-nums mt-0.5">{value}</p>
  </div>
);

export const CapTableIntegrityPanel = ({ instrumentId, refreshKey }) => {
  const [data, setData] = useState(null);
  const [checking, setChecking] = useState(false);
  const [open, setOpen] = useState(false);

  const run = useCallback(async () => {
    setChecking(true);
    try {
      const res = await api.get(`/admin/v2/captable/securities/${instrumentId}/integrity`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setChecking(false);
    }
  }, [instrumentId]);

  useEffect(() => { run(); }, [run, refreshKey]);

  if (!data) return null;

  const failing = data.checks.filter((c) => !c.ok);
  const issues = data.investor_issues || [];
  const problems = failing.length + issues.length;
  const healthy = data.ok;

  return (
    <div
      className={cn(
        'rounded-xl border mb-8 overflow-hidden',
        healthy ? 'border-black/[0.06] bg-white' : 'border-amber-300 bg-amber-50/60'
      )}
      data-testid="captable-integrity-panel"
    >
      <button
        className="w-full flex flex-wrap items-center justify-between gap-3 px-4 py-3 text-left"
        onClick={() => setOpen((v) => !v)}
        data-testid="captable-integrity-toggle"
      >
        <span className="flex items-center gap-2.5 min-w-0">
          {healthy ? (
            <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" weight="duotone" />
          ) : (
            <Warning className="h-5 w-5 text-amber-600 flex-shrink-0" weight="duotone" />
          )}
          <span className="min-w-0">
            <span className="text-sm font-semibold" data-testid="captable-integrity-status">
              {healthy
                ? 'Cap table balances'
                : `${problems} discrepanc${problems === 1 ? 'y' : 'ies'} to resolve`}
            </span>
            <span className="block text-xs text-foreground-muted">
              {units(data.totals.units_outstanding)} units in issue across {data.totals.holders}{' '}
              holder{data.totals.holders === 1 ? '' : 's'} · checked against holdings, events and subscriptions
            </span>
          </span>
        </span>
        <span className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost" size="sm"
            onClick={(e) => { e.stopPropagation(); run(); }}
            disabled={checking}
            data-testid="captable-integrity-recheck"
          >
            <ArrowsClockwise className={cn('h-4 w-4 mr-1.5', checking && 'animate-spin')} />
            Re-check
          </Button>
          <CaretRight className={cn('h-4 w-4 text-foreground-muted transition-transform', open && 'rotate-90')} />
        </span>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-4 border-t border-black/[0.06] pt-4" data-testid="captable-integrity-detail">
          <div className="flex flex-wrap gap-x-8 gap-y-3">
            <Figure label="Instrument figure" value={units(data.totals.units_outstanding)} />
            <Figure label="Sum of holdings" value={units(data.totals.position_units)} />
            <Figure label="Issued by events" value={units(data.totals.issued_units)} />
            <Figure label="From subscriptions" value={units(data.totals.subscription_units)} />
            <Figure label="Authorised" value={units(data.totals.authorised_units)} />
          </div>

          <ul className="space-y-1.5">
            {data.checks.map((check) => (
              <li
                key={check.key}
                className="flex items-start gap-2 text-[13px]"
                data-testid={`captable-check-${check.key}`}
              >
                {check.ok ? (
                  <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" weight="duotone" />
                ) : (
                  <Warning className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" weight="duotone" />
                )}
                <span>
                  {check.label}
                  {!check.ok && (
                    <span className="text-amber-800">
                      {' '}— off by {units(Math.abs(check.delta))} units ({units(check.actual)} vs{' '}
                      {units(check.expected)}). {check.detail}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>

          {issues.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-white p-3" data-testid="captable-integrity-investors">
              <p className="text-xs font-semibold uppercase tracking-[0.12em] text-foreground-muted mb-2">
                Holders whose figures disagree
              </p>
              <ul className="space-y-1.5 text-[13px]">
                {issues.map((issue) => (
                  <li key={issue.investor_id} className="flex flex-wrap items-center gap-x-3">
                    <span className="font-medium">{issue.investor_name}</span>
                    <span className="text-foreground-muted tabular-nums">
                      holds {units(issue.units_held)} units, subscriptions account for{' '}
                      {units(issue.units_from_subscriptions)}
                    </span>
                    {issue.units_delta !== 0 && (
                      <span className="text-amber-800 tabular-nums">
                        ({issue.units_delta > 0 ? '+' : ''}{units(issue.units_delta)})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-[11px] text-foreground-muted">
            Last checked {new Date(data.checked_at).toLocaleString('en-GB')}
          </p>
        </div>
      )}
    </div>
  );
};

export default CapTableIntegrityPanel;
